import { useEffect, useState } from 'react';

const DEFAULT_THEME = 'light';
const STORAGE_KEY = 'ivtracker_theme';

const readStoredTheme = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (err) {
    console.warn('Failed to read stored theme', err);
  }
  return null;
};

export function getDocumentTheme() {
  if (typeof document === 'undefined') return DEFAULT_THEME;

  const stored = readStoredTheme();
  if (stored) {
    return stored;
  }

  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') {
    return attr;
  }

  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }

  return DEFAULT_THEME;
}

export function setDocumentTheme(nextTheme) {
  if (typeof document === 'undefined') return;
  const normalized = nextTheme === 'light' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', normalized);
  document.documentElement.style.colorScheme = normalized === 'light' ? 'light' : 'dark';
  if (typeof window !== 'undefined') {
    try {
      window.localStorage?.setItem(STORAGE_KEY, normalized);
    } catch (err) {
      console.warn('Failed to persist theme preference', err);
    }
  }
}

export default function useDocumentTheme() {
  const [theme, setTheme] = useState(() => {
    return getDocumentTheme();
  });

  useEffect(() => {
    if (typeof document === 'undefined') return () => {};

    // Prerendered HTML may carry light while this visitor has dark saved.
    if (document.documentElement.getAttribute('data-theme') !== theme) {
      document.documentElement.setAttribute('data-theme', theme);
    }
    document.documentElement.style.colorScheme = theme === 'light' ? 'light' : 'dark';

    const observer = new MutationObserver(() => {
      setTheme(getDocumentTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, [theme]);

  return theme;
}
